import {
  useState, useEffect, useMemo, useRef,
} from 'react';
import {
  IconButton, Grid, Divider, Typography, Box,
  Button, Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Tabs,
  Tab
} from '@material-ui/core';
import { QRCodeSVG } from 'qrcode.react';
import Skeleton from '@material-ui/lab/Skeleton';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import KeyboardReturnIcon from '@material-ui/icons/KeyboardReturn';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import EditIcon from '@material-ui/icons/EditOutlined';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import fileDownload from 'js-file-download';

import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';

// components
import { useHistory, useLocation, useParams } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import Notification from '../../utils/notification';
import DeleteDialog from '../../components/DeleteDialog';
import TableData from '../../components/TableData';
import EditarCobrancaModal from './EditarCobrancaModal';

import { useLayoutDispatch, toggleLoading } from '../../context/LayoutContext';
import useStyles from './styles';
import api from '../../services/api';
import { getError } from '../../utils/getResponseError';
import Widget from '../../components/Widget/Widget';
import { toCurrency } from '../../utils/mask';
import {
  dateWithHours, dateWithTimeZone, parseISO, basicDate
} from '../../utils/dateFormatter';
import { ESTADO_FATURA } from '../../constants/estado-fatura';

export default function CobrancaDetalhes() {
  const history = useHistory();
  const { id } = useParams();
  const classes = useStyles();
  const [cobranca, setCobranca] = useState();
  const [faturas, setFaturas] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [fatura, setFatura] = useState(null);
  const [openFatura, setOpenFatura] = useState(null);
  const [finalizar, setfinalizar] = useState(null);
  const [loadingFatura, setLoadingFatura] = useState(false);
  const [loadingEncerrar, setloadingEncerrar] = useState(false);
  const [motivo, setMotivo] = useState('ACERTOS');
  const [proximoEstado, setProximoEstado] = useState(6);
  const [editarOpen, setEditarOpen] = useState(false);
  const pollingRef = useRef(null);
  const layoutDispatch = useLayoutDispatch();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [tabValue, setTabValue] = useState(params.get('tab') === 'historico' ? 1 : 0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', newValue === 1 ? 'historico' : 'faturas');
    history.replace({ search: newParams.toString() });
  };

  const handleViewHistorico = () => {
    setTabValue(1);
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', 'historico');
    history.replace({ search: newParams.toString() });
  };

  const historicoData = useMemo(() => {
    if (!cobranca || !cobranca.CobrancaEdicoes) return [];
    return cobranca.CobrancaEdicoes.map((edicao) => ({
      data: edicao.editado_em ? dateWithHours(parseISO(edicao.editado_em)) : '—',
      usuario: edicao.Usuario ? edicao.Usuario.nome.split(' ')[0] : '—',
      dia_vencimento: { anterior: edicao.anterior_dia_vencimento, posterior: edicao.posterior_dia_vencimento },
      dias_antes: { anterior: edicao.anterior_dias_antes, posterior: edicao.posterior_dias_antes },
      valor: { anterior: edicao.anterior_valor, posterior: edicao.posterior_valor },
      data_termino: { anterior: edicao.anterior_data_termino, posterior: edicao.posterior_data_termino },
      multa_juros: {
        vencido_aceita: { anterior: edicao.anterior_vencido_aceita, posterior: edicao.posterior_vencido_aceita },
        multa: { anterior: edicao.anterior_vencido_multa, posterior: edicao.posterior_vencido_multa },
        juros: { anterior: edicao.anterior_vencido_juros, posterior: edicao.posterior_vencido_juros },
        dias_max: { anterior: edicao.anterior_vencido_dias_max, posterior: edicao.posterior_vencido_dias_max },
      },
      desconto_bloco: {
        desconto: { anterior: edicao.anterior_desconto, posterior: edicao.posterior_desconto },
        valor: { anterior: edicao.anterior_desconto_valor, posterior: edicao.posterior_desconto_valor },
        prazo: { anterior: edicao.anterior_desconto_prazo, posterior: edicao.posterior_desconto_prazo },
      },
    }));
  }, [cobranca]);

  const arrowRender = (value) => {
    if (value.anterior === null && value.posterior === null) return '—';
    return `${value.anterior ?? '—'} → ${value.posterior ?? '—'}`;
  };

  const historicoColumns = useMemo(() => {
    const pValor = cobranca?.Produto?.valor;
    const produtoLabel = pValor ? `valor do produto (${toCurrency(pValor, true)})` : 'valor do produto';
    return [
      { name: 'data', label: 'Data', options: { filter: false, sort: true } },
      { name: 'usuario', label: 'Usuário', options: { filter: false, sort: false } },
      {
        name: 'dia_vencimento',
        label: 'Dia Venc.',
        options: {
          filter: false,
          sort: false,
          customBodyRender: arrowRender,
        },
      },
      {
        name: 'dias_antes',
        label: 'Dias Antes',
        options: {
          filter: false,
          sort: false,
          customBodyRender: arrowRender,
        },
      },
      {
        name: 'valor',
        label: 'Valor',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => {
            if (value.anterior === null && value.posterior === null) return '—';
            const ant = value.anterior !== null ? toCurrency(value.anterior, true) : produtoLabel;
            const post = value.posterior !== null ? toCurrency(value.posterior, true) : produtoLabel;
            return `${ant} → ${post}`;
          },
        },
      },
      {
        name: 'data_termino',
        label: 'Data Término',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => {
            if (value.anterior === null && value.posterior === null) return '—';
            const ant = value.anterior ? basicDate(parseISO(value.anterior)) : 'não definida';
            const post = value.posterior ? basicDate(parseISO(value.posterior)) : 'não definida';
            return `${ant} → ${post}`;
          },
        },
      },
      {
        name: 'multa_juros',
        label: 'Multa e Juros',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (bloco) => {
            const parts = [];
            const {
              vencido_aceita: vencidoAceita,
              multa,
              juros,
              dias_max: diasMax,
            } = bloco;

            if (vencidoAceita.anterior !== null || vencidoAceita.posterior !== null) {
              const ant = vencidoAceita.anterior ? 'Sim' : 'Não';
              const post = vencidoAceita.posterior ? 'Sim' : 'Não';
              if (ant !== post) parts.push(`Aceita: ${ant}→${post}`);
            }
            if (multa.anterior !== null || multa.posterior !== null) {
              const ant = multa.anterior != null ? `${multa.anterior}%` : '—';
              const post = multa.posterior != null ? `${multa.posterior}%` : '—';
              if (ant !== post) parts.push(`Multa: ${ant}→${post}`);
            }
            if (juros.anterior !== null || juros.posterior !== null) {
              const ant = juros.anterior != null ? `${juros.anterior}%` : '—';
              const post = juros.posterior != null ? `${juros.posterior}%` : '—';
              if (ant !== post) parts.push(`Juros: ${ant}→${post}`);
            }
            if (diasMax.anterior !== null || diasMax.posterior !== null) {
              const ant = diasMax.anterior != null ? `${diasMax.anterior}d` : '—';
              const post = diasMax.posterior != null ? `${diasMax.posterior}d` : '—';
              if (ant !== post) parts.push(`Prazo: ${ant}→${post}`);
            }
            return parts.length > 0 ? parts.join(' · ') : '—';
          },
        },
      },
      {
        name: 'desconto_bloco',
        label: 'Desconto',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (bloco) => {
            const parts = [];
            const { desconto, valor, prazo } = bloco;

            if (desconto.anterior !== null || desconto.posterior !== null) {
              const ant = desconto.anterior ? 'Ativo' : 'Inativo';
              const post = desconto.posterior ? 'Ativo' : 'Inativo';
              if (ant !== post) parts.push(`${ant}→${post}`);
            }
            if (valor.anterior !== null || valor.posterior !== null) {
              const ant = valor.anterior != null ? toCurrency(valor.anterior, true) : '—';
              const post = valor.posterior != null ? toCurrency(valor.posterior, true) : '—';
              if (ant !== post) parts.push(`${ant}→${post}`);
            }
            if (prazo.anterior !== null || prazo.posterior !== null) {
              const ant = prazo.anterior != null ? `${prazo.anterior}d` : '—';
              const post = prazo.posterior != null ? `${prazo.posterior}d` : '—';
              if (ant !== post) parts.push(`${ant}→${post}`);
            }
            return parts.length > 0 ? parts.join(' · ') : '—';
          },
        },
      },
    ];
  }, [cobranca]);

  const faturaColumns = useMemo(() => [
    {
      name: 'id_fatura',
      label: '#',
      options: { filter: true, sort: true },
    },
    {
      name: 'vencimento',
      label: 'Vencimento',
      options: { filter: true, sort: true },
    },
    {
      name: 'estado',
      label: 'Estado',
      options: { filter: true, sort: false },
    },
    {
      name: 'criado_em',
      label: 'Última atualização',
      options: { filter: true, sort: false },
    },
  ], []);

  const getStatusColor = (estadoId) => {
    switch (estadoId) {
      case ESTADO_FATURA.PAGO:
        return 'success';
      case ESTADO_FATURA.PENDENTE:
        return 'warning';
      case ESTADO_FATURA.PROCESSANDO:
        return 'primary';
      case ESTADO_FATURA.VENCIDO:
        return 'error';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    if (!id) {
      history.push('/app/cobrancas');
      return;
    }
    cobrancaRequest();
  }, [id]);

  // Cleanup polling on unmount
  useEffect(() => () => stopPolling(), []);

  useEffect(() => {
    faturasRequest();
  }, [page, id]);

  async function cobrancaRequest() {
    toggleLoading(layoutDispatch);

    try {
      const { data } = await api.get(`/cobranca/${id}`);
      setCobranca(data);
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
      history.push('/app/cobrancas');
    }
    toggleLoading(layoutDispatch);
  }

  function handlePage(newPage) {
    if (newPage !== page) {
      setPage(newPage);
    }
  }

  async function faturasRequest(showLoading) {
    setLoadingFatura(true);
    try {
      const { data } = await api.get(`/cobranca/${id}/faturas?pagina=${page}`);
      setTotal(data.totalPaginas || data.total || 1);
      setFaturas(data.dados.map((fatura) => ({
        ...fatura,
        estado: fatura.EstadosFatura.nome,
        criado_em: fatura.atualizado_em ? dateWithHours(parseISO(fatura.atualizado_em)) : dateWithHours(parseISO(fatura.criado_em)),
        vencimento: basicDate(parseISO(fatura.vencimento))
      })));
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
    }
    setLoadingFatura(false);
  }

  function handleCloseFatura() {
    setFatura(null);
    stopPolling();
  }

  function startPolling(faturaId, faturaIndex) {
    stopPolling(); // Clear any existing polling

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/fatura/${faturaId}`);

        // Update fatura state
        setFatura(data);

        // Update faturas array if status changed
        setFaturas((prevFaturas) => {
          const updatedFaturas = [...prevFaturas];
          if (updatedFaturas[faturaIndex]) {
            updatedFaturas[faturaIndex] = {
              ...updatedFaturas[faturaIndex],
              estado: data.EstadosFatura.nome,
              criado_em: data.atualizado_em ? dateWithHours(parseISO(data.atualizado_em)) : dateWithHours(parseISO(data.criado_em))
            };
          }
          return updatedFaturas;
        });

        // Stop polling if status is no longer PENDENTE, PROCESSANDO, or VENCIDO
        if (data.id_estado_fatura !== ESTADO_FATURA.PENDENTE && data.id_estado_fatura !== ESTADO_FATURA.PROCESSANDO && data.id_estado_fatura !== ESTADO_FATURA.VENCIDO) {
          stopPolling();
        }
      } catch (error) {
        console.error('Error polling fatura:', error);
        stopPolling();
      }
    }, 5000); // Poll every 5 seconds

    pollingRef.current = interval;
  }

  function stopPolling() {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }

  async function copyToClipboard(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      Notification({
        type: 'shipped',
        message: `${label} copiado para a área de transferência`,
        variant: 'contained',
        color: 'success',
      });
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        Notification({
          type: 'shipped',
          message: `${label} copiado para a área de transferência`,
          variant: 'contained',
          color: 'success',
        });
      } catch (fallbackError) {
        Notification({
          type: 'report',
          message: 'Erro ao copiar para a área de transferência',
          variant: 'contained',
          color: 'secondary',
        });
      }
      document.body.removeChild(textArea);
    }
  }

  async function handleShow(index) {
    toggleLoading(layoutDispatch);
    const find = faturas[index];
    try {
      const { data } = await api.get(`/fatura/${find.id_fatura}`);
      setFatura(data);

      // Start polling if fatura is PENDENTE, PROCESSANDO, or VENCIDO
      if (data.id_estado_fatura === ESTADO_FATURA.PENDENTE || data.id_estado_fatura === ESTADO_FATURA.PROCESSANDO || data.id_estado_fatura === ESTADO_FATURA.VENCIDO) {
        startPolling(find.id_fatura, index);
      }
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
    }
    toggleLoading(layoutDispatch);
  }

  async function handleBaixa() {
    const day = new Date().getDay();

    if (day === 0 || day === 6) {
      Notification({
        type: 'report',
        message: 'Apenas é possível dar baixa em dias úteis',
        variant: 'contained',
        color: 'secondary',
      });
      return;
    }

    toggleLoading(layoutDispatch);
    try {
      const find = faturas.findIndex((f) => f.id_fatura === openFatura);
      await api.post(`/fatura/baixa/${openFatura}`, {
        motivo,
        proximoEstado
      });

      Notification({
        type: 'shipped',
        message: 'Sucesso',
        variant: 'contained',
        color: 'success',
      });
      faturasRequest();
      handleShow(find);
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
    }
    setOpenFatura(null);
    toggleLoading(layoutDispatch);
  }
  async function handleEncerrar() {
    setloadingEncerrar(true);
    try {
      await api.put(`/cobranca/finalizar/${finalizar}`);
      Notification({
        type: 'shipped',
        message: 'Fatura encerrada',
        variant: 'contained',
        color: 'success',
      });

      cobrancaRequest();
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
    }
    setfinalizar(null);
    setloadingEncerrar(false);
  }
  async function handlePDF() {
    try {
      const { data } = await api.get(`/fatura/${fatura.nosso_numero}/download`, {
        responseType: 'blob'
      });
      fileDownload(data, `fatura_${fatura.id_fatura}.pdf`);
    } catch (error) {
      Notification({
        type: 'report',
        message: 'Erro ao gerar o boleto',
        variant: 'contained',
        color: 'secondary',
      });
    }
  }
  return (
    <>
      <DeleteDialog
        open={!!openFatura}
        handleClose={() => setOpenFatura(null)}
        handleConfirm={handleBaixa}
        message={openFatura ? `Tem certeza que deseja dar baixa na fatura #${openFatura}?` : ''}
        deleteText="dar baixa"
        title="Baixa manual"
      >
        <Grid
          container
          spacing={4}
        >
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Motivo da baixa (para o banco)</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={motivo}
                name="dias_antes"
                onChange={(e) => setMotivo(e.target.value)}
                required
              >
                <MenuItem value="ACERTOS">Baixa para acertos</MenuItem>
                <MenuItem value="PAGODIRETOAOCLIENTE">Baixado, pago direto ao cliente</MenuItem>
                <MenuItem value="APEDIDODOCLIENTE">Baixado a pedido do cliente</MenuItem>
                <MenuItem value="SUBSTITUICAO">Baixado para substituição</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Estado da fatura</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={proximoEstado}
                name="dias_antes"
                onChange={(e) => setProximoEstado(e.target.value)}
                required
              >
                <MenuItem value={2}>Pago</MenuItem>
                <MenuItem value={4}>Cancelado</MenuItem>
                <MenuItem value={6}>Baixa</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DeleteDialog>
      <DeleteDialog
        open={!!finalizar}
        handleClose={() => {
          if (!loadingEncerrar) {
            setfinalizar(null);
          }
          return null;
        }}
        handleConfirm={handleEncerrar}
        message={finalizar ? `Tem certeza que deseja finalizar a fatura #${finalizar}?` : ''}
        deleteText={loadingEncerrar ? <CircularProgress size={20} /> : 'Encerrar'}
      />
      <EditarCobrancaModal
        open={editarOpen}
        onClose={() => setEditarOpen(false)}
        cobranca={cobranca}
        onSaved={cobrancaRequest}
        onFaturaEmitida={faturasRequest}
        layoutDispatch={layoutDispatch}
        onViewHistorico={handleViewHistorico}
      />
      <Dialog
        onClose={handleCloseFatura}
        aria-labelledby="customized-dialog-title"
        open={fatura}
        BackdropProps={{
          className: classes.backdrop
        }}
        fullWidth
        maxWidth="lg"
      >
        <MuiDialogTitle id="customized-dialog-title">
          Detalhes da fatura
          {' '}
          {fatura && `#${fatura.id_fatura}`}
        </MuiDialogTitle>
        <MuiDialogContent dividers>
          {fatura && (
            <>
              <Grid container spacing={3}>
                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} mb={3}>
                    {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          type="button"
                          onClick={() => {
                            if (fatura) {
                              setOpenFatura(fatura.id_fatura);
                            }
                          }}
                        >
                          Baixa
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          type="button"
                          onClick={handlePDF}
                        >
                          PDF
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>

                {/* First Row: Details | Pix */}
                <Grid item xs={12} md={6}>
                  {/* Fatura Details */}
                  <Typography variant="h6" gutterBottom align="center">
                    Detalhes da fatura
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2} p={2} border={1} borderColor="grey.200" borderRadius={8}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" color="textSecondary">
                        ID Banco Inter:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {fatura.banco_inter}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" color="textSecondary">
                        Estado atual:
                      </Typography>
                      <Chip
                        size="small"
                        label={fatura.EstadosFatura.nome}
                        color={getStatusColor(fatura.id_estado_fatura)}
                      />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" color="textSecondary">
                        Vencimento:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {basicDate(parseISO(fatura.vencimento))}
                      </Typography>
                    </Box>

                    <Divider />

                    {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) && (
                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle1" color="textSecondary">
                            Código de barras:
                          </Typography>
                          <Tooltip title="Copiar código de barras">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(fatura.codigo_de_barras, 'Código de barras')}
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="body2" style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {fatura.codigo_de_barras}
                        </Typography>

                      </Box>
                    )}

                    <Box mt={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" color="textSecondary">
                          Link da fatura:
                        </Typography>
                        <Tooltip title="Copiar link da fatura">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(`${process.env.REACT_APP_BASE_URL}/#/fatura/${fatura.nosso_numero}`, 'Link da fatura')}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="body2"
                        style={{
                          wordBreak: 'break-all',
                          fontFamily: 'monospace',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          color: '#1976d2'
                        }}
                        onClick={() => window.open(`${process.env.REACT_APP_BASE_URL}/#/fatura/${fatura.nosso_numero}`, '_blank')}
                      >
                        {`${process.env.REACT_APP_BASE_URL}/#/fatura/${fatura.nosso_numero}`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  {/* PIX QR Code Section */}
                  {(() => {
                    if ((fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO || fatura.id_estado_fatura === ESTADO_FATURA.VENCIDO) && fatura.pix_copia_e_cola) {
                      return (
                        <Box display="flex" flexDirection="column" alignItems="center" p={2} border={1} borderColor="grey.300" borderRadius={8}>
                          <Typography variant="h6" gutterBottom align="center">
                            PIX QR Code
                          </Typography>
                          <Box mb={2}>
                            <QRCodeSVG
                              value={fatura.pix_copia_e_cola}
                              size={200}
                              level="M"
                              includeMargin
                            />
                          </Box>
                          <Typography variant="body2" color="textSecondary" align="center" mb={2}>
                            Escaneie o QR Code para pagar via PIX
                          </Typography>

                          <Box width="100%" mb={2}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="subtitle2" color="textSecondary">
                                PIX Copia e Cola:
                              </Typography>
                              <Tooltip title="Copiar código PIX">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(fatura.pix_copia_e_cola, 'Código PIX')}
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Typography variant="body2" style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {fatura.pix_copia_e_cola}
                            </Typography>
                          </Box>

                          {(fatura.id_estado_fatura === ESTADO_FATURA.PENDENTE || fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO) && (
                            <Box mt={1}>
                              <Chip
                                size="small"
                                label={fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO ? 'Processando pagamento...' : 'Aguardando pagamento...'}
                                color={fatura.id_estado_fatura === ESTADO_FATURA.PROCESSANDO ? 'primary' : 'warning'}
                                icon={<CircularProgress size={12} />}
                                style={{ padding: '8px 12px' }}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    }

                    if (fatura.id_estado_fatura === ESTADO_FATURA.PAGO) {
                      return (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          p={4}
                          border={1}
                          borderColor="success.light"
                          borderRadius={8}
                          minHeight={300}
                          bgcolor="success.light"
                          style={{ backgroundColor: '#e8f5e8' }}
                        >
                          <Typography variant="h6" color="success.dark" align="center" gutterBottom>
                            ✅ Pagamento Confirmado
                          </Typography>
                          <Typography variant="body1" color="success.dark" align="center">
                            Esta fatura foi paga com sucesso
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4} border={1} borderColor="grey.200" borderRadius={8} minHeight={300}>
                        <Typography variant="body1" color="textSecondary" align="center">
                          QR Code PIX não disponível
                        </Typography>
                      </Box>
                    );
                  })()}
                </Grid>

                {/* Second Row: Historico | Notifications */}
                <Grid item xs={12} md={6}>
                  {/* Historico da fatura */}
                  <Typography variant="h6" gutterBottom align="center">
                    Histórico da fatura
                  </Typography>
                  {fatura && fatura.HistoricoFaturas ? (
                    <TableContainer component={Paper} style={{ overflowX: 'auto', minWidth: 300 }}>
                      <Table size="small" aria-label="historico table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Estado</TableCell>
                            <TableCell align="right">Data</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fatura.HistoricoFaturas.map((row) => (
                            <TableRow key={row.id_historico_fatura}>
                              <TableCell component="th" scope="row">
                                {row.EstadosFatura.nome}
                              </TableCell>
                              <TableCell align="right">{dateWithHours(parseISO(row.criado_em))}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" p={4} border={1} borderColor="grey.200" borderRadius={8}>
                      <Typography variant="body2" color="textSecondary">
                        Nenhum histórico disponível
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  {/* Notificações */}
                  <Typography variant="h6" gutterBottom align="center">
                    Notificações enviadas
                  </Typography>
                  {fatura && fatura.Envios ? (
                    <TableContainer component={Paper} style={{ overflowX: 'auto', minWidth: 400 }}>
                      <Table size="small" aria-label="notificacoes table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Meio</TableCell>
                            <TableCell align="right">Estado</TableCell>
                            <TableCell align="right">Data</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fatura.Envios.map((row) => (
                            <TableRow key={row.id_historico_fatura}>
                              <TableCell component="th" scope="row">
                                {row.ServicosDeEnvio.nome}
                              </TableCell>
                              <TableCell align="right">{row.EstadosEnvio.nome}</TableCell>
                              <TableCell align="right">
                                {row.atualizado_em ? dateWithHours(parseISO(row.atualizado_em))
                                  : dateWithHours(parseISO(row.criado_em))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" p={4} border={1} borderColor="grey.200" borderRadius={8}>
                      <Typography variant="body2" color="textSecondary">
                        Nenhuma notificação enviada
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </MuiDialogContent>
        <MuiDialogActions>
          <Button
            autoFocus
            onClick={handleCloseFatura}
            color="primary"
          >
            Fechar
          </Button>
        </MuiDialogActions>
      </Dialog>
      <PageTitle
        title={`Cobrança #${id}`}
        button={(
          <div>
            {cobranca && cobranca.recorrente && !cobranca.terminado && (
              <Tooltip title="Editar cobrança" aria-label="editar-cobranca">
                <IconButton
                  color="primary"
                  aria-label="editar-cobranca"
                  className={classes.addButton}
                  style={{ marginRight: 20 }}
                  onClick={() => setEditarOpen(true)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Gerar contrato" aria-label="pdf-contrato">
              <IconButton
                color="primary"
                aria-label="pdf-contrato"
                className={classes.addButton}
                style={{ marginRight: 20 }}
                onClick={() => { }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>

            <IconButton
              color="primary"
              aria-label="cadastrar novo cliente"
              className={classes.addButton}
              onClick={() => history.push('/app/cobrancas')}
            >
              <KeyboardReturnIcon />
            </IconButton>
          </div>

        )}
      />
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <Widget
            disableWidgetMenu
            styleBody={classes.font16}
            header={(
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="h5" color="textSecondary" noWrap>
                  Detalhes da cobrança
                </Typography>
              </Box>
            )}
          >
            {cobranca && (
              <>
                <p>
                  Cliente:
                  {' '}
                  {cobranca.Cliente.nome}
                </p>
                <p>
                  Produto:
                  {' '}
                  {cobranca.Produto.nome}
                </p>
                <p>
                  Valor do produto:
                  {' '}
                  {toCurrency(cobranca.Produto.valor, true)}
                </p>
                {cobranca.valor !== null && cobranca.valor !== undefined && (
                  <p>
                    Valor diferenciado:
                    {' '}
                    {toCurrency(cobranca.valor, true)}
                  </p>
                )}
                <p>
                  Primeiro vencimento:
                  {' '}
                  {dateWithTimeZone(parseISO(cobranca.primeiro_vencimento))}
                </p>
                <p>
                  Dia de vencimento:
                  {' '}
                  {cobranca.dia_vencimento < 10 ? `0${cobranca.dia_vencimento}` : cobranca.dia_vencimento}
                </p>
                {cobranca.recorrente && (
                  <p>
                    Enviar
                    {' '}
                    {cobranca.dias_antes === 1 ? '1 dia' : `${cobranca.dias_antes} dias`}
                    {' '}
                    antes
                  </p>
                )}
                <p>
                  Tipo da cobrança:
                  {' '}
                  {cobranca.recorrente ? 'recorrente' : 'individual'}
                  {cobranca.recorrente && !cobranca.terminado && (
                    <Button
                      autoFocus
                      onClick={() => setfinalizar(id)}
                      color="secondary"
                      variant="contained"
                      size="small"
                      style={{
                        marginLeft: 15
                      }}
                    >
                      encerrar
                    </Button>
                  )}
                  {cobranca.terminado && (
                    <Chip
                      style={{
                        marginLeft: 15
                      }}
                      size="small"
                      label="terminado"
                      color="sucess"
                    />
                  )}
                </p>
                {cobranca.data_termino && (
                  <>
                    <p>
                      Término programado para:
                      {' '}
                      {dateWithTimeZone(parseISO(cobranca.data_termino))}
                    </p>
                    Finalizado:
                    {' '}
                    {cobranca.terminado ? 'sim' : 'não'}
                  </>
                )}
              </>
            )}
          </Widget>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Widget
            disableWidgetMenu
            title="Opções do pagamento"
          >
            {cobranca && (

              <>
                <p>
                  Aceita pagamento após o vencimento:
                  {' '}
                  {cobranca.vencido_aceita ? 'sim' : 'não'}
                </p>
                {cobranca.vencido_aceita && (
                  <>
                    <p>
                      Máximo de dias:
                      {' '}
                      {cobranca.vencido_dias_max}
                    </p>
                    <p>
                      Multa:
                      {' '}
                      {cobranca.vencido_multa}
                      %
                    </p>
                    <p>
                      Juros:
                      {' '}
                      {cobranca.vencido_juros}
                      %
                    </p>
                  </>
                )}

                <Divider className={classes.divider} />
                <p>
                  Aplicar desconto de atecedência:
                  {' '}
                  {cobranca.desconto ? 'sim' : 'não'}
                </p>
                {cobranca.desconto && (
                  <>
                    <p>
                      Prazo do desconto:
                      {' '}
                      {cobranca.desconto_prazo}
                      {' '}
                      dias
                    </p>
                    <p>
                      Desconto:
                      {' '}
                      {toCurrency(cobranca.desconto_valor)}
                      %
                    </p>
                  </>
                )}
              </>
            )}
          </Widget>
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          {loadingFatura ? (
            <Skeleton animation="wave" className={classes.loading} />
          ) : (
            <Widget
              disableWidgetMenu
              bodyClass={classes.flatTable}
              header={(
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Faturas" />
                  <Tab label="Histórico de Edições" />
                </Tabs>
              )}
            >
              {(() => {
                if (tabValue === 0) {
                  return (
                    <TableData
                      title=""
                      columns={faturaColumns}
                      data={faturas}
                      page={page}
                      total={total}
                      onSetPage={handlePage}
                      onDetails={handleShow}
                      showDetails
                    />
                  );
                }
                if (historicoData.length > 0) {
                  return (
                    <TableData
                      title=""
                      columns={historicoColumns}
                      data={historicoData}
                      page={1}
                      total={1}
                      showDetails={false}
                      hideOptions
                    />
                  );
                }
                return (
                  <Box py={4} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      Nenhuma edição registrada.
                    </Typography>
                  </Box>
                );
              })()}
            </Widget>
          )}
        </Grid>
      </Grid>
    </>
  );
}
